import React, { useState } from 'react';
import SanctionsioScreeningCard from './SanctionsioScreeningCard';
import DetailDrawer from '../../common/DetailDrawer';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { createKycNestedSanctionComment, worldCheckHits, sanctionIoHits, amlWatcherHits } from "../../../API/Api";
import { toast } from 'react-toastify';
import { checkAPICallsQuota } from '../../shared/utils';
import KycQuotaModal from '../../shared/KycQuotaModal';


const SanctionsScreeningSectionLaw = ({userId, decodedToken, readOnly, setFormData, qnCounter, formData, kycData, crimesHitsLoading }) => {
  const [assessmentRunning, setAssessmentRunning] = useState(false);
  const [screeningSource, setScreeningSource] = useState('sanctions.io');
  const [apiKey, setApiKey] = useState(''); // for worldCheck
  const [apiSecret, setApiSecret] = useState(''); // for worldCheck
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ isOpen: false, summary: null }); // 新增状态管理抽屉

  // 打开详情抽屉
  const openDetailDrawer = (summary) => {
    setDetailDrawer({ isOpen: true, summary });
  };

  // 关闭详情抽屉
  const closeDetailDrawer = () => {
    setDetailDrawer({ isOpen: false, summary: null });
  };

  const handleSubmitNestedArrayComment = async (e, formName, nestedName, index) => {
    if (formData[formName][index][nestedName].trim() === '') { return }
 
    const newCommentEntry = {
      screenedDate: formData.screenedDate,
      kycaml_client_id: userId,
      question: formName + ' ' + index.toString(),
      content: formData[formName][index][nestedName],
      name: decodedToken.name,
      commented_at: DateTime.now().setZone('Asia/Singapore').toFormat("dd-MM-yyyy, HH:mm:ss"),
      comment_id: uuidv4()  
    }
    await createKycNestedSanctionComment(newCommentEntry);   
    formData[formName][index].comments.push(newCommentEntry);
    const newCommentArray = formData[formName][index].comments;

    const newPepArray = [...formData[formName]];
    newPepArray[index].comments = newCommentArray;
    newPepArray[index].newComment = '';

    setFormData((prevState) => ({
      ...prevState,
      [formName]: newPepArray
    }));
  }

  const handleNestedArrayInputChange = (e, nestedName, index) => {
    let { name, value } = e.target; 
    name = name.split(' ')[0];
    const newArrayResult = [...formData[name]];
    newArrayResult[index][nestedName] = value;

    const newFormData = {  
      ...formData,          
      [name]: newArrayResult
    };
    setFormData(newFormData); 
  }

  const handleRunAssessment = async (screeningSource) => {
    // 500-global 走 worldCheck（用户自带 key），不占额度，排除检查
    const quotaExceeded = await checkAPICallsQuota(setShowQuotaModal, '500-global');
    if (quotaExceeded) { return; }

    setAssessmentRunning(true);
    toast.info(`Assessment for ${screeningSource} running... `);

    try {
      const screenedName = kycData.name;
      const entityType = kycData.type.toUpperCase();

      if (['500-global'].includes(decodedToken.companyId)) { // for now hardcore 500 to only use worldCheck
        const payload = { 
          apiKey, apiSecret, 
          screenedName, 
          connectedParty: kycData.details?.connectedParty, 
          entityType: entityType, 
          kycaml_client_id: kycData['_id'],
          screenedDate: formData['screenedDate'], 
          screeningSource: 'worldCheck'
        };
        
        const worldCheckResponse = await worldCheckHits(payload);

        if (worldCheckResponse.data.message === 'ERR_BAD_REQUEST') { 
          toast.error('Invalid worldCheck API key/secret!');
        } else {
          const {pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, questionApiUrl} = worldCheckResponse.data;
          setFormData({...formData, pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, screeningSource, questionApiUrl});
          toast.success(`Assessment complete!`);
        }

      } else if (decodedToken?.kycSettings?.apiType === 'amlwatcher') {
        const payload = { 
          apiKey, apiSecret, 
          screenedName, 
          connectedParty: kycData.details?.connectedParty, 
          entityType: entityType, 
          kycaml_client_id: kycData['_id'],
          screenedDate: formData['screenedDate'], 
          screeningSource: 'amlwatcher'
        };
        
        const amlwatcherResponse = await amlWatcherHits(payload);

        if (amlwatcherResponse.data.quotaExceeded) {
          setShowQuotaModal(amlwatcherResponse.data.subscriptionExpired ? 'expired' : true);
        } else if (amlwatcherResponse.data.message === 'ERR_BAD_REQUEST') { 
          toast.error('Invalid amlwatcher API key/secret!');
        } else {
          const {pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, questionApiUrl} = amlwatcherResponse.data;
          setFormData({...formData, pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, screeningSource, questionApiUrl});
          toast.success(`Assessment complete!`);
        }
      } else {
        const payload = { 
          screenedName: kycData['name'],
          connectedParty: kycData?.details?.connectedParty,
          entityType: kycData['type'],
          kycaml_client_id: kycData['_id'],
          screenedDate: formData['screenedDate'],
          screeningSource: 'sanctions.io' 
        };

        const sanctionsResponseData = (await sanctionIoHits(payload)).data;
        if (sanctionsResponseData.quotaExceeded) {
          setShowQuotaModal(sanctionsResponseData.subscriptionExpired ? 'expired' : true);
        } else {
          const {pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, questionApiUrl} = sanctionsResponseData;
          setFormData({...formData, pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, screeningSource, questionApiUrl});
          toast.success(`Assessment complete!`);
        }
      }

    } catch(e) {
      console.log(e);
      toast.error('Something went wrong...');
    }

    setAssessmentRunning(false);
    return;
  }

  // RCA 提示框内容（保持英文原样）
  const rcaTooltip = 'RCA (Relatives and Close Associates) - Immediate family members or close social or professional contacts of a government or political official, or senior executive – meaning spouses, parents, siblings, children, and spouses’ parents and siblings.';

  return (
    <div>
      <KycQuotaModal
        status={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
      />

      {/* 详情抽屉 */}
      <DetailDrawer 
        isOpen={detailDrawer.isOpen} 
        onClose={closeDetailDrawer} 
        summary={detailDrawer.summary} 
      />
      
      {(decodedToken.companyId === 'Test_Company' || decodedToken.companyId === 'ambridge_Advisers' || decodedToken.companyId === '00_Global') && ( 
        <>
          <div className="flex space-x-4">
            <select value={screeningSource} onChange={(e) => setScreeningSource(e.target.value)} className="w-2/12 py-1 px-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500">
              <option value="" disabled> - Select Screening Source- </option>
              <option value="sanctions.io">Sanctions.io</option>
              <option value="worldCheck">World Check</option>
            </select>
            
            {screeningSource === 'worldCheck' && (
              <>
                <input maxLength={800} placeholder="Enter WorldCheck API Key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="w-3/12 py-1 px-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500" />
                <input maxLength={800} type='password' placeholder="Enter WorldCheck API Secret" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} className="w-5/12 py-1 px-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500" />
              </>
            )}
      
            <button onClick={() => handleRunAssessment(screeningSource)} disabled={assessmentRunning || readOnly} className={`w-2/12 py-1 px-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg ${assessmentRunning || readOnly ? 'opacity-30' : 'opacity-100'}`}>
              Run Screening
            </button>
          </div>
          <u> Showing results for: {formData.screeningSource} </u>
        </>
      )}

      <SanctionsioScreeningCard 
        disabled={readOnly} 
        questionNumber={qnCounter++} 
        handleSubmitComment={handleSubmitNestedArrayComment} 
        result={formData} 
        inputName={"pepHits"} 
        pepExtra={kycData} 
        handleInputChange={handleNestedArrayInputChange} 
        question={"Were there any hits for PEP screening?"}
        onOpenDetail={openDetailDrawer}
        tableHeaders={[
          {
            displayTitle: 'Name/Summary',
            width: 'w-3/12',
            dataField: (result) => (
              <div className="relative flex items-center space-x-2 group">
                <button 
                  className="text-blue-500 hover:text-blue-700 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title="View details"
                >
                  ℹ️
                </button>
                <span>{`${result?.name} - \n ${result?.title || ''}`}</span>
                {result?.matched_rca && (
                  <div className="relative">
                    <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                      RCA
                    </span>
                    <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64">
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          { displayTitle: 'Data Source', width: 'w-1.5/12', dataField: (result) => (
            <>
              {/* 处理 dataSource 或 dataSources 两种情况 */}
              {result?.dataSources?.length > 0 ? (
                result?.dataSources.map((dataSource, index) => (
                  <div key={index}>
                    {dataSource?.url ? (
                      <span>
                        {dataSource?.publisher}
                        <a className="text-blue-500" href={dataSource.url} target="_blank" rel="noopener noreferrer">
                          <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </span>
                    ) : (
                      <span>{dataSource?.publisher} N/A</span>
                    )}
                  </div>
                ))
              ) : (
                <span>N/A</span>
              )}
            </>
          )},
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />
      
      <SanctionsioScreeningCard 
        disabled={readOnly} 
        questionNumber={qnCounter++} 
        handleSubmitComment={handleSubmitNestedArrayComment} 
        result={formData} 
        inputName={"crimesHits"} 
        handleInputChange={handleNestedArrayInputChange} 
        question={"Were there any hits for crime?"}
        onOpenDetail={openDetailDrawer}
        loading={crimesHitsLoading}
        tableHeaders={[
          {
            displayTitle: 'Name / DataSource',
            width: 'w-2/12',
            dataField: (result) => (
              <div className="relative flex items-center space-x-2 group">
                <button 
                  className="text-blue-500 hover:text-blue-700 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title="View details"
                >
                  ℹ️
                </button>
                <span>{`${result.name} \n ${result.dataSource}`}</span>
                {result?.matched_rca && (
                  <div className="relative">
                    <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                      RCA
                    </span>
                    <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64">
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          { displayTitle: 'Data Source', width: 'w-1.5/12', dataField: (result) => (
            <>
              {/* 处理 dataSource 或 dataSources 两种情况 */}
              {result?.dataSources?.length > 0 ? (
                result?.dataSources.map((dataSource, index) => (
                  <div key={index}>
                    {dataSource?.url ? (
                      <span>
                        {dataSource?.publisher}
                        <a className="text-blue-500" href={dataSource.url} target="_blank" rel="noopener noreferrer">
                          <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </span>
                    ) : (
                      <span>{dataSource?.publisher} N/A</span>
                    )}
                  </div>
                ))
              ) : (
                <span>N/A</span>
              )}
            </>
          )},
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />
      
      <SanctionsioScreeningCard 
        disabled={readOnly} 
        questionNumber={qnCounter++} 
        handleSubmitComment={handleSubmitNestedArrayComment} 
        result={formData} 
        inputName={"sanctionsHits"} 
        handleInputChange={handleNestedArrayInputChange} 
        question={"Were there any hits for sanctions?"}
        onOpenDetail={openDetailDrawer}
        tableHeaders={[
          {
            displayTitle: 'Name',
            width: 'w-3/12',
            dataField: (result) => (
              <div className="relative flex items-center space-x-2 group">
                <button 
                  className="text-blue-500 hover:text-blue-700 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title="View details"
                >
                  ℹ️
                </button>
                <span>{result.name}</span>
                {result?.matched_rca && (
                  <div className="relative">
                    <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                      RCA
                    </span>
                    <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64">
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          { displayTitle: 'Data Source', width: 'w-1.5/12', dataField: (result) => (
            <>
              {/* 处理 sanctionHits 的 dataSources 数据结构 */}
              {result?.dataSources?.length > 0 ? (
                (() => {
                  const firstDataSource = result.dataSources[0];
                  return (
                    <div className="space-y-1">
                      {/* Country */}
                      {firstDataSource?.country?.[0] && (
                        <div className="text-sm">
                          <span className="font-medium">Country: </span>
                          {firstDataSource.country[0]}
                        </div>
                      )}
                      
                      {/* Reason */}
                      {firstDataSource?.reason?.[0] && (
                        <div className="text-sm">
                          <span className="font-medium">Reason: </span>
                          {firstDataSource.reason[0]}
                        </div>
                      )}
                      
                      {/* Sanction Type */}
                      {firstDataSource?.sanction_type?.[0] && (
                        <div className="text-sm">
                          <span className="font-medium">Type: </span>
                          {firstDataSource.sanction_type[0]}
                        </div>
                      )}
                      
                      {/* Sanctioning Authority */}
                      {firstDataSource?.sanctioning_authority?.[0] && (
                        <div className="text-sm">
                          <span className="font-medium">Authority: </span>
                          {firstDataSource.sanctioning_authority[0]}
                        </div>
                      )}
                      
                      {/* Reference Link */}
                      {(firstDataSource?.sanction_source?.[0] || firstDataSource?.reference_link?.[0]) && (
                        <div className="text-sm">
                          <span className="font-medium">Reference: </span>
                          <a 
                            className="text-blue-500" 
                            href={firstDataSource.sanction_source?.[0] || firstDataSource.reference_link?.[0]} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <span>N/A</span>
              )}
            </>
          )},
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />
      
      {decodedToken?.kycSettings?.apiType !== 'amlwatcher' && (
        <SanctionsioScreeningCard 
          disabled={readOnly} 
          questionNumber={qnCounter++} 
          handleSubmitComment={handleSubmitNestedArrayComment} 
          result={formData} 
          inputName={"highRiskJurisdictionHits"} 
          handleInputChange={handleNestedArrayInputChange} 
          question={"Were there any hits for High Risk Jurisdiction?"}
          onOpenDetail={openDetailDrawer}
          tableHeaders={[
            {
              displayTitle: 'Name',
              width: 'w-3/12',
              dataField: (result) => (
                <div className="relative flex items-center space-x-2 group">
                  <button 
                    className="text-blue-500 hover:text-blue-700 mr-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (result?.summary) openDetailDrawer(result.summary);
                    }}
                    title="View details"
                  >
                    ℹ️
                  </button>
                  <span>{result.name}</span>
                  {result?.matched_rca && (
                    <div className="relative">
                      <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                        RCA
                      </span>
                      <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64">
                        {rcaTooltip}
                      </div>
                    </div>
                  )}
                </div>
              ),
            },
            { 
              displayTitle: 'Data Source', 
              width: 'w-3/12', 
              dataField: (result) => {
                return ( 
                  <>
                    {result.dataSource} 
                    <a className='text-blue-500' href={result?.source_list_url} target="_blank" rel="noopener noreferrer"> {result.source_list_url} </a>
                  </> );
              }
            },
            { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
            { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
          ]}
        />
      )}
      
      <SanctionsioScreeningCard 
        disabled={readOnly} 
        questionNumber={qnCounter++} 
        handleSubmitComment={handleSubmitNestedArrayComment} 
        result={formData} 
        inputName={"adverseNewsHits"} 
        handleInputChange={handleNestedArrayInputChange} 
        question={"Are there any adverse news?"}
        onOpenDetail={openDetailDrawer}
        tableHeaders={[
          {
            displayTitle: 'Title',
            width: 'w-2.5/12',
            dataField: (result) => (
              <div className="relative flex items-center space-x-2 group">
                <button 
                  className="text-blue-500 hover:text-blue-700 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title="View details"
                >
                  ℹ️
                </button>
                <span>{result.title}</span>
                {result?.matched_rca && (
                  <div className="relative">
                    <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                      RCA
                    </span>
                    <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64">
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          { 
            displayTitle: 'Description', 
            width: 'w-3.5/12', 
            dataField: (result) => {
              return ( 
                <>
                  {result.description} 
                  <a className='text-blue-500' href={result?.url} target="_blank" rel="noopener noreferrer"> Link </a>
                </> );
            }
          },
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      /> 
    </div>
  );
}

export default SanctionsScreeningSectionLaw;